import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient, errorDetail } from '@/lib/apiClient';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { EditWithHistory } from '@/components/catalog/EditWithHistory';
import { VariationsDrawer } from '@/components/catalog/VariationsDrawer';

interface RequiredResource {
  resourceId: string; name: string; type: string;
  mandatory: boolean; setupUrl?: string | null; notes?: string | null;
}

type ProductType = 'LICENSE' | 'TEMPLATE' | 'TOOLKIT' | 'MODULE' | 'HARDWARE' | 'OTHER';
type LicenseModel = 'perpetual' | 'subscription' | 'per-seat' | 'usage-based' | 'one-time';
type SupportTier = 'free' | 'basic' | 'pro' | 'enterprise';

interface ProductItem {
  id: string;
  productId: string;
  name: string;
  type: ProductType;
  description: string | null;
  licenseModel: LicenseModel | null;
  manufacturingCostUsd: number;
  maintenanceCostMrrUsd: number;
  priceOneTimeUsd: number;
  priceMrrUsd: number;
  warrantyMonths: number | null;
  warrantyTerms: string | null;
  supportTier: SupportTier | null;
  countries: string[];
  tags: string[];
  requiredResources: RequiredResource[];
  notes: string | null;
  status: 'ACTIVE' | 'DRAFT' | 'ARCHIVED';
}

interface UpsertPayload {
  productId: string; name: string; type: ProductType;
  description: string | null;
  licenseModel: LicenseModel | null;
  manufacturingCostUsd: number; maintenanceCostMrrUsd: number;
  priceOneTimeUsd: number; priceMrrUsd: number;
  warrantyMonths: number | null; warrantyTerms: string | null;
  supportTier: SupportTier | null;
  countries: string[]; tags: string[];
  requiredResources: RequiredResource[];
  notes: string | null; status: 'ACTIVE' | 'DRAFT' | 'ARCHIVED';
}

interface PagedResult<T> { items: T[]; total: number; limit: number; offset: number; }
interface Facets {
  types: string[]; countries: string[]; tags: string[];
  statuses: string[]; supportTiers: string[]; licenseModels: string[];
}

const EMPTY: UpsertPayload = {
  productId: '', name: '', type: 'LICENSE',
  description: null, licenseModel: 'subscription',
  manufacturingCostUsd: 0, maintenanceCostMrrUsd: 0,
  priceOneTimeUsd: 0, priceMrrUsd: 0,
  warrantyMonths: null, warrantyTerms: null, supportTier: 'basic',
  countries: [], tags: [], requiredResources: [],
  notes: null, status: 'ACTIVE',
};

const PAGE_SIZE = 25;

function usd(n: number | null | undefined): string {
  if (n == null) return '—';
  return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }).format(n);
}
function marginPct(price: number, cost: number): string {
  if (!price || price <= 0) return '—';
  return `${(((price - cost) / price) * 100).toFixed(0)}%`;
}

export function ProductsCatalogPage() {
  const { t } = useTranslation();
  const [variationsProduct, setVariationsProduct] = useState<ProductItem | null>(null);
  const [items, setItems] = useState<ProductItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<ProductItem | null>(null);
  const [creating, setCreating] = useState(false);

  const [search, setSearch] = useState('');
  const [country, setCountry] = useState('');
  const [type, setType] = useState('');
  const [supportTier, setSupportTier] = useState('');
  const [statusFilter, setStatusFilter] = useState('ACTIVE');
  const [tag, setTag] = useState('');
  const [page, setPage] = useState(0);

  const [facets, setFacets] = useState<Facets>({
    types: [], countries: [], tags: [],
    statuses: ['ACTIVE', 'DRAFT', 'ARCHIVED'],
    supportTiers: ['free', 'basic', 'pro', 'enterprise'],
    licenseModels: ['perpetual', 'subscription', 'per-seat', 'usage-based', 'one-time'],
  });

  useEffect(() => {
    apiClient.get<Facets>('/admin/products-catalog/facets')
      .then(({ data }) => setFacets((prev) => ({ ...prev, ...data })))
      .catch(() => {});
  }, []);

  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 250);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (debouncedSearch) params.set('search', debouncedSearch);
    if (country) params.set('country', country);
    if (type) params.set('type', type);
    if (supportTier) params.set('support_tier', supportTier);
    if (statusFilter) params.set('status', statusFilter);
    if (tag) params.set('tag', tag);
    params.set('limit', String(PAGE_SIZE));
    params.set('offset', String(page * PAGE_SIZE));
    apiClient.get<PagedResult<ProductItem>>(`/admin/products-catalog/search?${params}`)
      .then(({ data }) => { setItems(data.items); setTotal(data.total); })
      .catch(() => { setItems([]); setTotal(0); })
      .finally(() => setLoading(false));
  }, [debouncedSearch, country, type, supportTier, statusFilter, tag, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(0); }, [debouncedSearch, country, type, supportTier, statusFilter, tag]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const clearFilters = () => {
    setSearch(''); setCountry(''); setType(''); setSupportTier('');
    setStatusFilter('ACTIVE'); setTag('');
  };
  const hasActiveFilters = Boolean(search || country || type || supportTier || tag || statusFilter !== 'ACTIVE');

  const handleArchive = (id: string) => {
    if (!confirm('¿Archivar este producto?')) return;
    apiClient.delete(`/admin/products-catalog/${id}`).then(() => load()).catch(() => {});
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Products Catalog</h1>
          <p className="text-sm text-muted-foreground">
            Catálogo MAESTRO Lopettia · licencias · módulos · templates · toolkits
          </p>
        </div>
        <Button size="sm" onClick={() => setCreating(true)}>+ Producto</Button>
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-2 rounded-lg border bg-card p-3">
        <Input placeholder="🔎 buscar…" value={search} onChange={(e) => setSearch(e.target.value)} className="w-64" />
        <select className="rounded border bg-background px-2 py-1.5 text-sm" value={country} onChange={(e) => setCountry(e.target.value)}>
          <option value="">país (todos)</option>
          {facets.countries.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select className="rounded border bg-background px-2 py-1.5 text-sm" value={type} onChange={(e) => setType(e.target.value)}>
          <option value="">tipo (todos)</option>
          {facets.types.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <select className="rounded border bg-background px-2 py-1.5 text-sm" value={supportTier} onChange={(e) => setSupportTier(e.target.value)}>
          <option value="">support (todos)</option>
          {facets.supportTiers.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select className="rounded border bg-background px-2 py-1.5 text-sm" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">status (todos)</option>
          {facets.statuses.map((s) => <option key={s} value={s}>{s.toLowerCase()}</option>)}
        </select>
        {facets.tags.length > 0 && (
          <select className="rounded border bg-background px-2 py-1.5 text-sm" value={tag} onChange={(e) => setTag(e.target.value)}>
            <option value="">tag (todos)</option>
            {facets.tags.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        )}
        {hasActiveFilters && <Button size="sm" variant="ghost" onClick={clearFilters}>limpiar</Button>}
        <div className="ml-auto text-xs text-muted-foreground">
          {total} {total === 1 ? 'resultado' : 'resultados'}
          {totalPages > 1 && ` · página ${page + 1} de ${totalPages}`}
        </div>
      </div>

      {creating && <ProductForm initial={EMPTY} onCancel={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
      {loading && <p className="text-sm text-muted-foreground">cargando…</p>}

      <div className="overflow-hidden rounded-lg border bg-card">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-4 py-2 text-left">producto</th>
              <th className="px-4 py-2 text-left">tipo</th>
              <th className="px-4 py-2 text-left">países</th>
              <th className="px-4 py-2 text-right">setup</th>
              <th className="px-4 py-2 text-right">MRR</th>
              <th className="px-4 py-2 text-right">margen</th>
              <th className="px-4 py-2 text-left">warranty</th>
              <th className="px-4 py-2 text-left">support</th>
              <th className="px-4 py-2 text-left">status</th>
              <th className="px-4 py-2 text-right">acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {items.map((p) => (
              <>
                <tr key={p.id} className={p.status === 'ARCHIVED' ? 'opacity-50' : ''}>
                  <td className="px-4 py-2">
                    <div className="font-medium">{p.name}</div>
                    <div className="font-mono text-xs text-muted-foreground">{p.productId}</div>
                  </td>
                  <td className="px-4 py-2"><span className="rounded bg-purple-100 px-2 py-0.5 text-xs text-purple-900 dark:bg-purple-900/30 dark:text-purple-200">{p.type}</span></td>
                  <td className="px-4 py-2">
                    <div className="flex flex-wrap gap-1">
                      {p.countries.length === 0 ? (
                        <span className="text-xs text-muted-foreground">global</span>
                      ) : p.countries.map((c) => (
                        <span key={c} className="rounded bg-blue-100 px-2 py-0.5 text-xs text-blue-900 dark:bg-blue-900/30 dark:text-blue-200">{c}</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-2 text-right font-mono">{usd(p.priceOneTimeUsd)}</td>
                  <td className="px-4 py-2 text-right font-mono">{usd(p.priceMrrUsd)}</td>
                  <td className="px-4 py-2 text-right font-mono text-xs">
                    {p.priceMrrUsd > 0 ? marginPct(p.priceMrrUsd, p.maintenanceCostMrrUsd) : marginPct(p.priceOneTimeUsd, p.manufacturingCostUsd)}
                  </td>
                  <td className="px-4 py-2 text-xs text-muted-foreground">
                    {p.warrantyMonths ? `${p.warrantyMonths} mo` : '—'}
                  </td>
                  <td className="px-4 py-2 text-xs">
                    {p.supportTier ? <span className="rounded bg-muted px-2 py-0.5">{p.supportTier}</span> : '—'}
                  </td>
                  <td className="px-4 py-2">
                    <span className={p.status === 'ACTIVE' ? 'text-xs text-green-600' : p.status === 'DRAFT' ? 'text-xs text-yellow-600' : 'text-xs text-muted-foreground'}>
                      {p.status}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-right">
                    <button onClick={() => setEditing(p)} className="text-xs text-primary hover:underline">editar</button>
                    <button onClick={() => setVariationsProduct(p)} className="ml-3 text-xs text-primary hover:underline" data-testid="product-variations">{t('catalogVariations.openVariations')}</button>
                    {p.status === 'ACTIVE' && (
                      <button onClick={() => handleArchive(p.id)} className="ml-3 text-xs text-muted-foreground hover:underline">archivar</button>
                    )}
                  </td>
                </tr>
                {editing?.id === p.id && (
                  <tr>
                    <td colSpan={10} className="bg-muted/20 px-4 py-4">
                      <EditWithHistory entityType="product" entityId={p.id} editForm={<ProductForm initial={toPayload(p)} editingId={p.id} onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />} />
                    </td>
                  </tr>
                )}
              </>
            ))}
            {!loading && items.length === 0 && (
              <tr><td colSpan={10} className="px-4 py-8 text-center text-sm text-muted-foreground">
                {hasActiveFilters ? 'sin resultados' : 'sin productos · click "+ Producto"'}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between rounded-lg border bg-card px-4 py-2 text-sm">
          <div className="text-xs text-muted-foreground">mostrando {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} de {total}</div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>← anterior</Button>
            <Button size="sm" variant="outline" disabled={page >= totalPages - 1} onClick={() => setPage((p) => p + 1)}>siguiente →</Button>
          </div>
        </div>
      )}

      {variationsProduct && (
        <VariationsDrawer
          parentProductId={variationsProduct.id}
          productName={variationsProduct.name}
          onClose={() => setVariationsProduct(null)}
        />
      )}
    </div>
  );
}

function toPayload(p: ProductItem): UpsertPayload {
  return {
    productId: p.productId, name: p.name, type: p.type, description: p.description,
    licenseModel: p.licenseModel,
    manufacturingCostUsd: p.manufacturingCostUsd, maintenanceCostMrrUsd: p.maintenanceCostMrrUsd,
    priceOneTimeUsd: p.priceOneTimeUsd, priceMrrUsd: p.priceMrrUsd,
    warrantyMonths: p.warrantyMonths, warrantyTerms: p.warrantyTerms, supportTier: p.supportTier,
    countries: p.countries, tags: p.tags, requiredResources: p.requiredResources,
    notes: p.notes, status: p.status,
  };
}

function ProductForm({ initial, editingId, onCancel, onSaved }: {
  initial: UpsertPayload; editingId?: string; onCancel: () => void; onSaved: () => void;
}) {
  const [form, setForm] = useState<UpsertPayload>(initial);
  const [countriesText, setCountriesText] = useState(initial.countries.join(', '));
  const [tagsText, setTagsText] = useState(initial.tags.join(', '));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null); setSubmitting(true);
    try {
      const body: UpsertPayload = {
        ...form,
        countries: countriesText.split(',').map((s) => s.trim().toUpperCase()).filter(Boolean),
        tags: tagsText.split(',').map((s) => s.trim()).filter(Boolean),
      };
      if (editingId) {
        await apiClient.put(`/admin/products-catalog/${editingId}`, body);
      } else {
        await apiClient.post('/admin/products-catalog', body);
      }
      onSaved();
    } catch (err) {
      setError(errorDetail(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      {error && <div className="rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800">{error}</div>}

      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <label className="space-y-1">
          <span className="text-xs font-medium uppercase text-muted-foreground">product_id</span>
          <Input value={form.productId} onChange={(e) => setForm({ ...form, productId: e.target.value })} placeholder="kairos-crm" required />
        </label>
        <label className="space-y-1">
          <span className="text-xs font-medium uppercase text-muted-foreground">name</span>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
        </label>
        <label className="space-y-1">
          <span className="text-xs font-medium uppercase text-muted-foreground">type</span>
          <select className="w-full rounded border bg-background px-3 py-1.5 text-sm" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value as ProductType })}>
            <option value="LICENSE">LICENSE</option>
            <option value="TEMPLATE">TEMPLATE</option>
            <option value="TOOLKIT">TOOLKIT</option>
            <option value="MODULE">MODULE</option>
            <option value="HARDWARE">HARDWARE</option>
            <option value="OTHER">OTHER</option>
          </select>
        </label>
      </div>

      <label className="block space-y-1">
        <span className="text-xs font-medium uppercase text-muted-foreground">description</span>
        <textarea className="w-full rounded border bg-background px-3 py-2 text-sm" rows={2} value={form.description ?? ''} onChange={(e) => setForm({ ...form, description: e.target.value || null })} />
      </label>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <label className="space-y-1">
          <span className="text-xs font-medium uppercase text-muted-foreground">países (ISO · vacío = global)</span>
          <Input value={countriesText} onChange={(e) => setCountriesText(e.target.value)} placeholder="PR, CO, US" />
        </label>
        <label className="space-y-1">
          <span className="text-xs font-medium uppercase text-muted-foreground">tags</span>
          <Input value={tagsText} onChange={(e) => setTagsText(e.target.value)} placeholder="crm, flagship" />
        </label>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <label className="space-y-1">
          <span className="text-xs font-medium uppercase text-muted-foreground">price setup USD</span>
          <Input type="number" step="0.01" value={form.priceOneTimeUsd} onChange={(e) => setForm({ ...form, priceOneTimeUsd: parseFloat(e.target.value) || 0 })} />
        </label>
        <label className="space-y-1">
          <span className="text-xs font-medium uppercase text-muted-foreground">price MRR USD</span>
          <Input type="number" step="0.01" value={form.priceMrrUsd} onChange={(e) => setForm({ ...form, priceMrrUsd: parseFloat(e.target.value) || 0 })} />
        </label>
        <label className="space-y-1">
          <span className="text-xs font-medium uppercase text-muted-foreground">cost setup (manuf)</span>
          <Input type="number" step="0.01" value={form.manufacturingCostUsd} onChange={(e) => setForm({ ...form, manufacturingCostUsd: parseFloat(e.target.value) || 0 })} />
        </label>
        <label className="space-y-1">
          <span className="text-xs font-medium uppercase text-muted-foreground">cost MRR (maint)</span>
          <Input type="number" step="0.01" value={form.maintenanceCostMrrUsd} onChange={(e) => setForm({ ...form, maintenanceCostMrrUsd: parseFloat(e.target.value) || 0 })} />
        </label>
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <label className="space-y-1">
          <span className="text-xs font-medium uppercase text-muted-foreground">license model</span>
          <select className="w-full rounded border bg-background px-3 py-1.5 text-sm" value={form.licenseModel ?? ''} onChange={(e) => setForm({ ...form, licenseModel: (e.target.value || null) as LicenseModel | null })}>
            <option value="">—</option>
            <option value="perpetual">perpetual</option>
            <option value="subscription">subscription</option>
            <option value="per-seat">per-seat</option>
            <option value="usage-based">usage-based</option>
            <option value="one-time">one-time</option>
          </select>
        </label>
        <label className="space-y-1">
          <span className="text-xs font-medium uppercase text-muted-foreground">warranty (meses)</span>
          <Input type="number" value={form.warrantyMonths ?? ''} onChange={(e) => setForm({ ...form, warrantyMonths: e.target.value ? parseInt(e.target.value) : null })} placeholder="12" />
        </label>
        <label className="space-y-1">
          <span className="text-xs font-medium uppercase text-muted-foreground">support tier</span>
          <select className="w-full rounded border bg-background px-3 py-1.5 text-sm" value={form.supportTier ?? ''} onChange={(e) => setForm({ ...form, supportTier: (e.target.value || null) as SupportTier | null })}>
            <option value="">—</option>
            <option value="free">free</option>
            <option value="basic">basic</option>
            <option value="pro">pro</option>
            <option value="enterprise">enterprise</option>
          </select>
        </label>
      </div>

      <label className="block space-y-1">
        <span className="text-xs font-medium uppercase text-muted-foreground">warranty terms</span>
        <textarea className="w-full rounded border bg-background px-3 py-2 text-sm" rows={2} value={form.warrantyTerms ?? ''} onChange={(e) => setForm({ ...form, warrantyTerms: e.target.value || null })} />
      </label>

      <label className="block space-y-1">
        <span className="text-xs font-medium uppercase text-muted-foreground">notes</span>
        <textarea className="w-full rounded border bg-background px-3 py-2 text-sm" rows={2} value={form.notes ?? ''} onChange={(e) => setForm({ ...form, notes: e.target.value || null })} />
      </label>

      <div className="flex justify-end gap-2">
        <Button variant="outline" size="sm" type="button" onClick={onCancel}>cancelar</Button>
        <Button size="sm" type="submit" disabled={submitting}>{submitting ? 'guardando…' : editingId ? 'guardar' : 'crear'}</Button>
      </div>
    </form>
  );
}
